import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { getApiUrl } from '../../config/api';
import UserManagement from './UserManagement';
import SystemMetrics from './SystemMetrics';
import UserActivity from './UserActivity';
import RolePermissions from './RolePermissions';
import AdminAnalytics from './AdminAnalytics';
import EmailActivity from './EmailActivity';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, isAdmin, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [systemHealth, setSystemHealth] = useState({
    status: 'loading',
    services: {},
    lastUpdated: null
  });

  useEffect(() => {
    if (isAdmin) {
      fetchSystemHealth();
      const interval = setInterval(fetchSystemHealth, 30000); // Update every 30 seconds
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const fetchSystemHealth = async () => {
    try {
      if (!user) {
        console.error('User not authenticated');
        return;
      }
      const token = await user.getIdToken();
      const response = await fetch(getApiUrl('/api/admin/analytics/system-metrics'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSystemHealth({
          status: 'healthy',
          services: data,
          lastUpdated: new Date().toLocaleTimeString()
        });
      } else {
        setSystemHealth({
          status: 'error',
          services: {},
          lastUpdated: new Date().toLocaleTimeString()
        });
      }
    } catch (error) {
      console.error('Error fetching system health:', error);
      setSystemHealth({
        status: 'error',
        services: {},
        lastUpdated: new Date().toLocaleTimeString()
      });
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-dashboard">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'emails', label: 'Email Activity', icon: '📧' },
    { id: 'system', label: 'System Metrics', icon: '⚙️' },
    { id: 'activity', label: 'User Activity', icon: '📈' },
    { id: 'roles', label: 'Role Management', icon: '🔐' },
    { id: 'analytics', label: 'Admin Analytics', icon: '📋' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <AdminOverview systemHealth={systemHealth} />;
      case 'users':
        return <UserManagement />;
      case 'emails':
        return <EmailActivity />;
      case 'system':
        return <SystemMetrics />;
      case 'activity':
        return <UserActivity />;
      case 'roles':
        return <RolePermissions />;
      case 'analytics':
        return <AdminAnalytics />;
      default:
        return <AdminOverview systemHealth={systemHealth} />;
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-info">
          <span>Welcome, {user?.email}</span>
          <span className="system-status">
            System: {systemHealth.status}
            {systemHealth.lastUpdated && (
              <span className="last-updated">
                Last updated: {systemHealth.lastUpdated}
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="admin-navigation">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="admin-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

// Overview component
const AdminOverview = ({ systemHealth }) => {
  const { user } = useAuth();
  const [quickStats, setQuickStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalEmails: 0,
    systemUptime: 0
  });

  useEffect(() => {
    if (user) {
      fetchQuickStats();
    }
  }, [user]);

  const fetchQuickStats = async () => {
    try {
      if (!user) {
        console.error('User not authenticated');
        return;
      }
      const token = await user.getIdToken();
      
      // Fetch user count
      const usersResponse = await fetch(getApiUrl('/api/admin/users'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setQuickStats(prev => ({
          ...prev,
          totalUsers: usersData.total || 0,
          activeUsers: usersData.users?.filter(u => u.is_active).length || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching quick stats:', error);
    }
  };

  return (
    <div className="admin-overview">
      <div className="overview-grid">
        <div className="stat-card">
          <h3>Total Users</h3>
          <div className="stat-value">{quickStats.totalUsers}</div>
          <div className="stat-label">Registered users</div>
        </div>
        
        <div className="stat-card">
          <h3>Active Users</h3>
          <div className="stat-value">{quickStats.activeUsers}</div>
          <div className="stat-label">Currently active</div>
        </div>
        
        <div className="stat-card">
          <h3>System Status</h3>
          <div className={`stat-value status-${systemHealth.status}`}>
            {systemHealth.status.toUpperCase()}
          </div>
          <div className="stat-label">Current health</div>
        </div>
        
        <div className="stat-card">
          <h3>Total Emails</h3>
          <div className="stat-value">94,558</div>
          <div className="stat-label">Processed emails</div>
        </div>
      </div>

      <div className="system-health-panel">
        <h3>System Health</h3>
        <div className="health-indicators">
          <div className="health-indicator">
            <span className="indicator-label">CPU Usage:</span>
            <span className="indicator-value">
              {systemHealth.services?.current?.cpu_usage?.toFixed(1) || 'N/A'}%
            </span>
          </div>
          <div className="health-indicator">
            <span className="indicator-label">Memory Usage:</span>
            <span className="indicator-value">
              {systemHealth.services?.current?.memory_usage?.toFixed(1) || 'N/A'}%
            </span>
          </div>
          <div className="health-indicator">
            <span className="indicator-label">Response Time:</span>
            <span className="indicator-value">
              {systemHealth.services?.historical?.avg_response_time?.toFixed(0) || 'N/A'}ms
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
